<template>
  <AdminLayout>
    <!-- Breadcrumb -->
    <template #breadcrumb>
      <breadcrumb-link view="AdminReporting" label="Reporting" :active="true" />
    </template>

    <div class="box">
      <!-- Page Title -->
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title">Reporting</h1>
            </div>
          </div>
        </div>
      </div>

      <hr />

      <!-- Export Section -->
      <div class="export-section">
        <div class="level">
          <div class="level-left">
            <h2 class="title is-5">
              <p class="subtitle is-6 has-text-grey mt-1">
                Click to export Inquiries and Blanket VOBs as a CSV file. All exports are logged below.
              </p>
            </h2>
          </div>
          <div class="level-right">
            <b-button type="is-primary" @click="showExportPopup = true">Export</b-button>
          </div>
        </div>
      </div>

      <hr />
    </div>

    <hr />
    <!-- Audit Log Section -->

    <div class="audit-log-section">
      <b-card class="p-4 shadow-sm rounded-lg">
        <!-- Header -->
        <div class="mb-3">
          <h2 class="title is-5 mb-2">Export Audit</h2>
        </div>
        <!-- Logs Filters Row -->
        <div class="mb-4 filters-row">
          <div class="columns is-variable is-4">
            <!-- Organization Filter -->
            <div class="column">
              <b-field label="Organization">
                <b-select v-model="selectedOrg" placeholder="All Organizations" @input="fetchAuditLogs" expanded>
                  <option :value="0">All Organizations</option>
                  <option v-for="org in organizationOptions" :value="org.id" :key="org.id">
                    {{ org.name }}
                  </option>
                </b-select>
              </b-field>
            </div>

            <!-- Entity Filter -->
            <div class="column">
              <b-field label="Entity">
                <b-select v-model="selectedEntity" placeholder="All Entity" @input="fetchAuditLogs" expanded>
                  <option :value="0">All Entity</option>
                  <option v-for="entity in entityOptions" :value="entity.id" :key="entity.id">
                    {{ entity.name }}
                  </option>
                </b-select>
              </b-field>
            </div>

            <!-- Reset Button -->
            <div class="column is-narrow">
              <b-button type="is-primary" @click="resetFilters">Clear</b-button>
            </div>
          </div>
        </div>


        <!-- Logs Table -->
        <b-table :data="auditLogs" :loading="loading" :detailed=true :show-detail-icon=true detail-key="id"
          detail-transition="fade" striped hoverable paginated per-page="10">
          <b-table-column field="type" label="Type" v-slot="props">
            <span class="tag is-primary is-light">{{ props.row.type }}</span>
          </b-table-column>

          <b-table-column field="action" label="Action" v-slot="props">
            <span class="tag is-primary is-light">{{ props.row.action }}</span>
          </b-table-column>


          <b-table-column field="user" label="User" v-slot="props">
            <span style="white-space: pre-line;">
              {{ formatUser(props.row.user) }}
            </span>
          </b-table-column>

          <b-table-column label="Date" v-slot="props">
            <span style="white-space: pre-line;">
              {{ formatDate(props.row.created_at) }}
            </span>
          </b-table-column>

          <b-table-column label="Download" v-slot="props" centered>
            <div class="is-flex is-justify-content-center is-align-items-center">
              <b-button size="is-small" type="is-primary" outlined @click="reDownloadRequest(props.row)">
                <b-icon icon="download" size="is-small"></b-icon>
              </b-button>
            </div>
          </b-table-column>


          <template #detail="props">
            <p>
              <strong>Organization:</strong>
              {{ props.row.organization?.name || '-' }}
              <br />
              <strong>Include Deleted:</strong>
              {{ props.row.request?.include_hidden?.[0] === 'true' ? 'Yes' : 'No' }}
              <br />
              <strong>Include Draft:</strong>
              {{ props.row.request?.include_draft?.[0] === 'true' ? 'Yes' : 'No' }}
              <br />
              <strong>Include Archived:</strong>
              {{ props.row.request?.include_archived?.[0] === 'true' ? 'Yes' : 'No' }}
              <br />
              <strong>From Date:</strong>
              {{ props.row.request?.start_date?.[0] || '-' }}
              <br />
              <strong>To Date:</strong>
              {{ props.row.request?.end_date?.[0] || '-' }}
            </p>
          </template>
          <!-- Empty State -->
          <template #empty>
            <section class="section has-text-centered">
              <b-icon icon="file-alert" size="is-large"></b-icon>
              <p class="mt-2">No audit logs found for this selection</p>
            </section>
          </template>
        </b-table>
      </b-card>
    </div>

    <!-- Export Popup Component -->
    <ExportPopup v-model="showExportPopup" :organizations="organizationOptions" :is-admin="true"
      @confirm="handleExport" />
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';
import ExportPopup from '../../components/ExportPopup.vue';
import { EXPORT_ENTITIES, EXPORT_FILE_TYPES } from '../../constants.js';
export default {
  name: 'AdminReporting',
  components: { AdminLayout, ExportPopup },
  data() {
    return {
      exporting: false,
      loading: false,
      organizationOptions: [],
      showExportPopup: false,
      auditLogs: [],
      selectedOrg: null,
      selectedEntity: null,
      entityOptions: [
        { id: EXPORT_ENTITIES.Inquiry, name: EXPORT_ENTITIES.Inquiry },
        { id: EXPORT_ENTITIES.BlanketVOB, name: EXPORT_ENTITIES.BlanketVOB },
      ]
    };
  },
  created() {
    this.fetchOrganizations();
    this.fetchAuditLogs();
  },
  methods: {
    resetFilters() {
      this.selectedOrg = null
      this.selectedEntity = null
      this.fetchAuditLogs();
    },
    formatDateMMDDYYYY(date) {
      if (!date) return '';
      // If already a string, assume it's already formatted correctly (MM/DD/YYYY)
      if (typeof date === 'string') {
        return date;
      }
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${month}/${day}/${year}\n${hours}:${minutes} ${ampm}`; // <— newline
    },
    formatUser(user) {
      if (!user) return '';
      return `${user.first_name} ${user.last_name}\n${user.email}`
    },
    reDownloadRequest(row) {
      if (!row || !row.request) {
        this.$buefy.toast.open({
          message: 'Request data not available for re-download',
          type: 'is-warning'
        });
        return;
      }

      const params = {
        fileType: EXPORT_FILE_TYPES.CSV,
        organizationId: row.organization?.id,
        entityType: row.type,
        includeDeleted: row.request.include_hidden?.[0] === 'true',
        includeDraft: row.request.include_draft?.[0] === 'true',
        includeArchived: row.request.include_archived?.[0] === 'true',
        startDate: row.request.start_date?.[0],
        endDate: row.request.end_date?.[0],
      };

      this.handleExport(params);
    },
    async fetchOrganizations() {
      try {
        this.loading = true;
        const response = await request.get('/admin/organizations');
        this.organizationOptions = response.data.organizations.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load organizations', type: 'is-danger' });
      } finally {
        this.loading = false;
      }
    },
    async handleExport(config) {
      this.exporting = true;
      try {
        const params = {
          file_type: config.fileType,
          include_hidden: false,
          include_draft: false,
          include_archived: false,
          start_date: config.startDate,
          end_date: config.endDate,
        };
        params.organization_id = config.organizationId ? config.organizationId : "";
        params.start_date = config.startDate ? this.formatDateMMDDYYYY(config.startDate) : ""
        params.end_date = config.endDate
          ? this.formatDateMMDDYYYY(config.endDate)
          : this.formatDateMMDDYYYY(new Date());
        params.include_hidden = config.includeDeleted ? "true" : "";
        params.include_draft = config.includeDraft ? "true" : "";
        params.include_archived = config.includeArchived ? "true" : "";
        let filename = '';
        let exportUrl = ''
        if (config.entityType == EXPORT_ENTITIES.Inquiry) {
          exportUrl = '/admin/inquiries/export'
          filename = 'inquiries';
        } else if (config.entityType == EXPORT_ENTITIES.BlanketVOB) {
          exportUrl = '/admin/blanket-vob-search/export'
          filename = 'blanket-vob';
        }
        const response = await request.get(exportUrl, {
          params,
          responseType: 'blob',
        });
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match && match[1]) filename = match[1];
        }
        link.href = url;
        link.setAttribute('download', `${filename}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.$buefy.toast.open({ message: 'Export started successfully', type: 'is-success' });
        this.fetchAuditLogs();
      } catch (e) {
        let message = 'Failed to export report';
        if (e.response.status === 500) {
          message = e.response.statusText;
        }
        this.$buefy.toast.open({ message, type: 'is-danger' });
      } finally {
        this.exporting = false;
      }
    },
    async fetchAuditLogs() {
      try {
        this.loading = true;
        const params = {};
        if (this.selectedOrg) params.organization_id = this.selectedOrg;
        if (this.selectedEntity) params.entity_type = this.selectedEntity;
        const response = await request.get('/admin/auditlog', { params });
        this.auditLogs = response.data.audit_logs || [];
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load audit logs', type: 'is-danger' });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
